import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';

const Impressum = () => {
  const { t } = useTranslation();
  const [impressum, setImpressum] = useState({
    company_name: '',
    company_form: '',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    country: '',
    managing_director: '',
    registration_court: '',
    registration_number: '',
    vat_id: '',
    email: '',
    phone: '',
    responsible_person: '',
    additional_info: '',
    agb_download_url: ''
  });

  useEffect(() => {
    fetchImpressum();
  }, []);

  const fetchImpressum = async () => {
    try {
      const response = await axios.get(`${API}/settings/impressum`);
      if (response.data) {
        setImpressum(response.data);
      }
    } catch (error) {
      console.error('Error fetching impressum:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="impressum-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 animate-fadeIn">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">{t('impressum.title')}</h1>

          {/* AGB Download Button */}
          {impressum.agb_download_url && (
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <a 
                href={impressum.agb_download_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('impressum.agbDownload')}
              </a>
            </div>
          )}

          <div className="space-y-6 text-slate-700">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.tmgTitle')}</h2>
              <p>
                {impressum.company_name && (
                  <>
                    <strong>{impressum.company_name}</strong>
                    {impressum.company_form && ` ${impressum.company_form}`}
                    <br />
                  </>
                )}
                {impressum.address_line1 && <>{impressum.address_line1}<br /></>}
                {impressum.address_line2 && <>{impressum.address_line2}<br /></>}
                {(impressum.postal_code || impressum.city) && (
                  <>{impressum.postal_code} {impressum.city}<br /></>
                )}
                {impressum.country && <>{impressum.country}<br /></>}
              </p>
            </section>

            {impressum.managing_director && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.representedByTitle')}</h2>
                <p>
                  {t('impressum.managingDirector')}<br />
                  {impressum.managing_director}
                </p>
              </section>
            )}

            {(impressum.email || impressum.phone) && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.contactTitle')}</h2>
                <p>
                  {impressum.email && (
                    <>
                      {t('impressum.email')} <a href={`mailto:${impressum.email}`} className="text-blue-600 hover:underline">{impressum.email}</a><br />
                    </>
                  )}
                  {impressum.phone && <>{t('impressum.phone')} {impressum.phone}<br /></>}
                </p>
              </section>
            )}

            {(impressum.registration_court || impressum.registration_number || impressum.vat_id) && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.registrationTitle')}</h2>
                <p>
                  {impressum.registration_court && <>{t('impressum.registrationCourt')} {impressum.registration_court}<br /></>}
                  {impressum.registration_number && <>{t('impressum.registrationNumber')} {impressum.registration_number}<br /></>}
                  {impressum.vat_id && <>{t('impressum.vatId')} {impressum.vat_id}<br /></>}
                </p>
              </section>
            )}

            {impressum.responsible_person && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.responsibleTitle')}</h2>
                <p>{impressum.responsible_person}</p>
              </section>
            )}

            {impressum.additional_info && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.additionalInfoTitle')}</h2>
                <div className="whitespace-pre-line">
                  {impressum.additional_info}
                </div>
              </section>
            )}

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('impressum.disclaimerTitle')}</h2>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('impressum.liabilityContentTitle')}</h3>
              <p className="mb-4">
                {t('impressum.liabilityContentText')}
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('impressum.liabilityLinksTitle')}</h3>
              <p className="mb-4">
                {t('impressum.liabilityLinksText')}
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('impressum.copyrightTitle')}</h3>
              <p>
                {t('impressum.copyrightText')}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impressum;
